import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FormQuery, FormResponse } from '@troia-v3/types';

import {
  Button,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SortableHeadCell,
  StatusPill,
  ToolbarSearch,
} from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { usePermissions } from '@/hooks/usePermissions';
import { useChecklistTemplates, useDeleteChecklistTemplate } from '../hooks/useChecklistTemplates';
import { getFormStatusMeta } from '@/modules/automation/forms/components/formStatusMeta';

type SortKey = 'name' | 'status' | 'fields';

/**
 * ChecklistTemplatesPage — listagem dos modelos (`Form` com `type: 'checklist'`)
 * usados para criar checklists. "Novo modelo" e "Editar" levam ao builder
 * compartilhado (`FormBuilderPage`, Task 14) em `/checklists/templates/new`
 * e `/checklists/templates/:id/edit`.
 *
 * Lê `/api/checklists/templates` (via `useChecklistTemplates`), NÃO `/api/forms`:
 * o item de menu que traz o usuário até aqui é liberado por `checklists:read`,
 * e as rotas de `forms` exigem permissão do módulo `forms` — quem tivesse
 * Checklists sem Formulários abria esta tela e tomava 403, sem caminho algum
 * para criar modelo. `getFormStatusMeta` segue vindo de `automation/forms`
 * (é só formatação de status, cross-module import é permitido no frontend).
 */
export const ChecklistTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  // `type` não vai no payload: a rota `/api/checklists/templates` já é fechada
  // em checklist no backend.
  const [query, setQuery] = useState<FormQuery>({ page: 1, limit: 20 });
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });

  const { data, isLoading, error } = useChecklistTemplates(query);

  const items = useMemo(() => data?.items ?? [], [data]);

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'status') cmp = getFormStatusMeta(a.status).rank - getFormStatusMeta(b.status).rank;
      else cmp = a.fields.length - b.fields.length;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const goToBuilder = (form?: FormResponse) => {
    navigate(form ? `/checklists/templates/${form.id}/edit` : '/checklists/templates/new');
  };

  const confirm = useConfirm();
  const deleteTemplate = useDeleteChecklistTemplate();
  const { hasModulePermission } = usePermissions();
  const canDelete = hasModulePermission('checklists', 'delete');

  // Modelo em uso em automação o backend recusa (409) com a mensagem que o
  // usuário lê — o toast do `useDeleteChecklistTemplate` mostra o texto dele.
  const handleDeleteClick = async (form: FormResponse) => {
    const ok = await confirm({
      title: 'Excluir modelo',
      message: `Excluir o modelo "${form.name}"? Checklists já criados com ele continuam e podem ser concluídos.`,
      confirmButtonText: 'Excluir',
      variant: 'danger',
    });
    if (ok) deleteTemplate.mutate(form.id);
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="checklist-templates-page">
      <PageToolbar
        left={
          <>
            <nav className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => navigate('/checklists')}
                className="text-sm font-medium truncate hidden sm:inline"
                style={{ color: 'var(--theme-muted-text)' }}
                data-testid="checklist-templates-breadcrumb-back"
              >
                Checklists
              </button>
              <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
              <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Modelos</span>
            </nav>
            {data?.total != null && (
              <span
                className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                data-testid="checklist-templates-count"
              >
                {data.total}
              </span>
            )}
          </>
        }
        center={
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar modelo…"
            data-testid="checklist-templates-search"
          />
        }
        right={
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => goToBuilder()}
            data-testid="checklist-templates-new"
          >
            Novo modelo
          </Button>
        }
        mobile={
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar modelo…"
            data-testid="checklist-templates-search-mobile"
          />
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }} data-testid="checklist-templates-error">
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar modelos: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando modelos..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="check-square"
              message="Nenhum modelo de checklist cadastrado"
              description={searchTerm ? 'Tente ajustar a busca.' : 'Crie o primeiro modelo para começar a atribuir checklists.'}
              actionLabel="Novo modelo"
              onAction={() => goToBuilder()}
            />
          )}

          {!isLoading && !error && items.length > 0 && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-3 md:hidden">
                {sortedItems.map((form) => {
                  const statusMeta = getFormStatusMeta(form.status);
                  return (
                    <div
                      key={form.id}
                      className="rounded-xl p-4 cursor-pointer"
                      style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
                      onClick={() => goToBuilder(form)}
                      data-testid={`checklist-template-card-${form.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>{form.name}</h3>
                        <StatusPill variant={statusMeta.variant} label={statusMeta.label} />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                          {form.fields.length} {form.fields.length === 1 ? 'campo' : 'campos'}
                        </p>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void handleDeleteClick(form); }}
                            className="p-1.5 rounded-lg"
                            style={{ color: 'var(--theme-muted-text)' }}
                            aria-label="Excluir modelo"
                            data-testid={`checklist-template-card-delete-${form.id}`}
                          >
                            <Icon name="trash" className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse" data-testid="checklist-templates-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SortableHeadCell label="Nome" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell label="Nº de campos" sortKey="fields" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-24" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((form) => {
                      const statusMeta = getFormStatusMeta(form.status);
                      return (
                        <tr
                          key={form.id}
                          className="cursor-pointer transition-colors border-t"
                          style={{ borderColor: 'var(--theme-card-border)' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          onClick={() => goToBuilder(form)}
                          data-testid={`checklist-template-row-${form.id}`}
                        >
                          <td className="px-4 py-3 text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>{form.name}</td>
                          <td className="px-4 py-3">
                            <StatusPill variant={statusMeta.variant} label={statusMeta.label} />
                          </td>
                          <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{form.fields.length}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); goToBuilder(form); }}
                              className="p-1.5 rounded-lg"
                              style={{ color: 'var(--theme-muted-text)' }}
                              aria-label="Editar modelo"
                              data-testid={`checklist-template-edit-${form.id}`}
                            >
                              <Icon name="edit" className="w-4 h-4" />
                            </button>
                            {canDelete && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); void handleDeleteClick(form); }}
                                className="p-1.5 rounded-lg"
                                style={{ color: 'var(--theme-muted-text)' }}
                                aria-label="Excluir modelo"
                                data-testid={`checklist-template-delete-${form.id}`}
                              >
                                <Icon name="trash" className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!isLoading && !error && data?.page != null && data?.totalPages != null && (data.total ?? 0) > 0 && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
              totalItems={data.total}
              pageSize={query.limit ?? 20}
              itemLabel="modelos"
            />
          )}
        </div>
      </div>
    </div>
  );
};

ChecklistTemplatesPage.displayName = 'ChecklistTemplatesPage';
